import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Box,
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel,
  FormControlProps,
  FormHelperText,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  SxProps,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { FieldInputProps, useField } from 'formik';
import React from 'react';
import { useTranslation } from '../../../../../../services/translation/hooks/useTranslation';
import { getErrorMessage } from '../../../../onboardingUtils';
import useFormStyles from '../../../hooks/useFormStyles';
import { FieldsArea, FieldsAreaGridConfig } from './FieldsArea';
import { FormFieldType } from './FormFieldType';

// Structure for select form menu item options.
export interface SelectOptions {
  label: string;
  value: string;
}

interface BaseFieldProps {
  type: FormFieldType;
  label: string;
}

export interface SelectFieldTypeProps extends BaseFieldProps {
  type: FormFieldType.Select;
  initialValue?: string;
  config?: Omit<SelectProps, 'value' | 'onChange'> & {
    items: SelectOptions[];
    onChangeAction?: (value?: string) => void;
  };
}

export interface TextFieldTypeProps extends BaseFieldProps {
  type: FormFieldType.TextField;
  initialValue?: string | null;
  config?: Omit<TextFieldProps, 'value' | 'onChange'>;
}

export interface TextDisplayTypeProps extends BaseFieldProps {
  type: FormFieldType.TextDisplay;
  initialValue?: string; // TODO: Added for unification, ignore in config
  config?: {
    labelSX?: SxProps;
    valueSX?: SxProps;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueGetter?: (value: FieldInputProps<any>) => string | number;
  };
}

export interface CheckboxTypeProps extends BaseFieldProps {
  type: FormFieldType.Checkbox;
  initialValue?: boolean;
  config?: Omit<CheckboxProps, 'value' | 'onChange'>;
}

export interface CheckboxDisplayTypeProps extends BaseFieldProps {
  type: FormFieldType.CheckboxDisplay;
  initialValue?: boolean;
  config?: {
    labelSX?: SxProps;
    valueSX?: SxProps;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueGetter?: (value: FieldInputProps<any>) => string | number;
  };
}

export interface NestedFieldTypeProps extends BaseFieldProps {
  type: FormFieldType.Nested;
  initialValue?: Record<string, unknown>;
  gridConfig?: FieldsAreaGridConfig;
  config?: Record<string, GenericFieldProps>;
}

export type GenericFieldProps = (
  | SelectFieldTypeProps
  | TextFieldTypeProps
  | TextDisplayTypeProps
  | CheckboxTypeProps
  | CheckboxDisplayTypeProps
  | NestedFieldTypeProps
) & {
  renderFn?: (props: FormFieldProps) => React.ReactElement;
};

// Props interface for all form fields, including common props and specific ones like selectOptions.
export type FormFieldProps = Pick<
  FormControlProps,
  'fullWidth' | 'required' | 'disabled' | 'size'
> & {
  name: string;
  helperText?: string;
  error?: boolean;
  fieldMeta: GenericFieldProps;
};

// The GenericFormField component, designed to render different types of input form fields based on the 'type' prop.
const GenericFormField: React.FC<FormFieldProps> = ({
  name,
  fieldMeta,
  ...props
}) => {
  const { t } = useTranslation();
  const [field, meta, helpers] = useField({ name });
  const { commonTextFieldStyles } = useFormStyles();
  const isError = meta.touched && Boolean(meta.error);
  const helperText = isError
    ? getErrorMessage(meta.error, t)
    : props.helperText ?? '';
  const { type, config, label, renderFn } = fieldMeta;

  // Common props applied to all field types, incorporating Formik's field state and additional props.
  const commonProps = {
    ...field,
    label: t(label || name, { ns: 'common' }),
    error: isError,
    ...props,
  };

  if (renderFn && typeof renderFn === 'function') {
    return renderFn({ name, fieldMeta, ...props });
  }

  /**
   * Handles changes to TextField inputs. For numeric fields, this function ensures
   * that empty strings are converted to null to prevent Formik from casting them to NaN.
   * This approach ensures compatibility with backends that accept null for numeric fields but not NaN or empty strings.
   *
   * @param event The input change event.
   */
  const handleTextFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Determine if the TextField is meant to handle numeric values based on the 'type' prop in config
    const type = config && 'type' in config ? config.type : undefined;
    const isNumeric = type === 'number';

    // If the field is numeric and the value is an empty string, set it to null; otherwise, use the original value
    const value =
      isNumeric && event.target.value === '' ? null : event.target.value;

    // For numeric fields with a non-empty value, convert the value to a number; otherwise, directly use the value
    const finalValue = isNumeric && value !== null ? Number(value) : value;

    // Call Formik's field helper to update the value with the appropriate final value
    helpers.setValue(finalValue);
  };

  /**
   * Handles the change event for a select field, updating the form state and invoking a custom change action if specified.
   * @param event The event object containing the new value of the select field.
   */
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;

    // If the config object contains an 'onChangeAction' function, call it with the selected value.  Useful for cascading dropdowns that filter options based on the selected values. For example, customer dropdown filtering ranch options.
    if (type === FormFieldType.Select && config?.onChangeAction) {
      config?.onChangeAction(value);
    }

    helpers.setValue(value); // Update Formik's field value
  };

  // Switch statement to render the appropriate form field type based on the 'type' prop.
  switch (type) {
    case FormFieldType.TextField:
      return (
        <TextField
          {...commonProps}
          helperText={t(helperText, { ns: 'common' })}
          variant='standard'
          color='secondary'
          sx={{ ...commonTextFieldStyles }}
          {...config}
          onChange={handleTextFieldChange}
        />
      );

    case FormFieldType.Select:
      return (
        <FormControl
          variant='filled'
          color='secondary'
          fullWidth
          error={isError}
          sx={{ color: 'white' }}
        >
          <InputLabel id={`${label}-helper-label`}>{label}</InputLabel>
          <Select
            {...commonProps}
            labelId={`${label}-helper-label`}
            onChange={handleSelectChange}
          >
            {config?.items?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{t(helperText, { ns: 'common' })}</FormHelperText>
        </FormControl>
      );

    case FormFieldType.TextDisplay: {
      const value = config?.valueGetter
        ? config.valueGetter(field)
        : field.value;
      let displayValue = 'Not Found';
      if (value) {
        displayValue = value.toString();
      }

      return (
        <Grid container>
          <Grid item xs={4} sx={config?.labelSX}>
            <Typography>{commonProps.label}</Typography>
          </Grid>
          <Grid item xs={7} sx={config?.valueSX}>
            <Typography>{displayValue}</Typography>
          </Grid>
        </Grid>
      );
    }

    case FormFieldType.Checkbox:
      return (
        <FormControlLabel
          control={
            <Checkbox
              {...field}
              {...config}
              checked={field.value}
              onChange={(e) => helpers.setValue(e.target.checked)}
            />
          }
          label={label}
        />
      );

    case FormFieldType.CheckboxDisplay: {
      const value = config?.valueGetter
        ? config.valueGetter(field)
        : commonProps.value;

      let displayElement;
      if (value === true || value === 'true') {
        displayElement = (
          <CheckCircleIcon
            sx={{ color: (theme) => theme.colors.common.bloomfieldGreen }}
          />
        );
      } else if (value === false || value === 'false') {
        displayElement = <RemoveCircleOutlineIcon />;
      } else {
        displayElement = (
          <Typography>{t('Not Found', { ns: 'common' })}</Typography>
        );
      }

      return (
        <Grid container>
          <Grid item xs={4} sx={config?.labelSX}>
            <Typography>{commonProps.label}</Typography>
          </Grid>
          <Grid item xs={7} sx={config?.valueSX}>
            {displayElement}
          </Grid>
        </Grid>
      );
    }

    case FormFieldType.Nested:
      if (!config) {
        console.warn('Nested form field config is missing');
        return null;
      }
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            width: '100%',
            borderRadius: '8px',
            border: '1px solid grey',
            p: 3,
          }}
        >
          <Typography>{commonProps.label}</Typography>
          <FieldsArea {...config.gridConfig}>
            {Object.entries(config)?.map(([nestedFieldKey, nestedField]) => (
              <GenericFormField
                key={`${name}.${nestedFieldKey}`}
                name={`${name}.${nestedFieldKey}`}
                fieldMeta={nestedField}
              />
            ))}
          </FieldsArea>
        </Box>
      );
    case undefined:
      return (
        <TextField
          {...commonProps}
          helperText={t(helperText, { ns: 'common' })}
          variant='standard'
          color='secondary'
          sx={{ ...commonTextFieldStyles }}
          {...(config as TextFieldProps)}
        />
      );

    default:
      console.warn(`Unsupported form field type: ${type}`);
      return null;
  }
};

export default GenericFormField;
